<template>
  <div class="vdi-product-selector-page">
    <!-- 邀请码认证弹窗 -->
    <el-dialog
      :title="null"
      :visible.sync="showAuthDialog"
      width="480px"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      class="auth-dialog"
    >
      <div class="auth-dialog-body">
        <div class="auth-header">
          <div class="auth-icon-wrapper">
            <i class="el-icon-key" />
          </div>
          <h3 class="auth-title">邀请码认证</h3>
          <p class="auth-description">访问此功能需要邀请码验证，请输入管理员提供的邀请码</p>
        </div>

        <!-- 表单 -->
        <el-form
          ref="authForm"
          :model="authForm"
          :rules="authRules"
          class="auth-form"
          @submit.native.prevent="handleValidate"
        >
          <el-form-item prop="inviteCode">
            <el-input
              v-model="authForm.inviteCode"
              placeholder="请输入邀请码"
              :disabled="authLoading"
              clearable
              size="large"
              @keyup.enter.native="handleValidate"
            >
              <template slot="prefix">
                <i class="el-icon-key" style="line-height: 40px; margin-left: 5px;" />
              </template>
            </el-input>
          </el-form-item>
        </el-form>

        <transition name="slide-fade">
          <div v-if="authError" class="auth-error">
            <i class="el-icon-warning" />
            <span>{{ authError }}</span>
          </div>
        </transition>

        <div class="auth-footer">
          <el-button type="primary" :loading="authLoading" @click="handleValidate" class="auth-submit">
            {{ authLoading ? '验证中...' : '提交' }}
          </el-button>
          <el-button @click="handleAuthCancel" :disabled="authLoading" class="auth-cancel">取消</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- iframe 主体 -->
    <div v-if="loading && authPassed" class="selector-loading">
      <i class="el-icon-loading"></i>
      <span>加载中...</span>
    </div>
    <iframe
      v-if="authPassed"
      ref="selectorFrame"
      :src="frameSrc"
      class="selector-frame"
      frameborder="0"
      @load="handleFrameLoad"
    />
  </div>
</template>

<script>
import { Button, Dialog, Input, Form } from 'element-ui'

import { validateInviteCode, createOpAudit } from '@/api/servicePrice'

export default {
  name: 'VdiProductSelector',
  components: {
    [Button.name]: Button,
    [Form.name]: Form,
    [Input.name]: Input,
    [Dialog.name]: Dialog
  },
  data() {
    return {
      loading: true,
      authPassed: false,
      currentInviteCode: null,
      inviteExpiresAt: null,
      frameSrc: '',

      // 认证弹窗
      showAuthDialog: false,
      authLoading: false,
      authError: '',
      authForm: {
        inviteCode: ''
      },
      authRules: {
        inviteCode: [
          { required: true, message: '请输入邀请码', trigger: 'blur' },
          { min: 5, message: '邀请码格式不正确', trigger: 'blur' }
        ]
      },

      // 防抖 timer
      auditDebounceTimer: null
    }
  },

  mounted() {
    const persisted = this.getPersistedInviteAuthorization()
    if (persisted) {
      this.currentInviteCode = persisted.inviteCode
      this.inviteExpiresAt = persisted.expiresAt
      this.authPassed = true
      this.frameSrc = '/selector/'
      this.showAuthDialog = false
    } else {
      this.showAuthDialog = true
    }
    window.addEventListener('message', this.handleMessage)
  },

  beforeDestroy() {
    window.removeEventListener('message', this.handleMessage)
    if (this.auditDebounceTimer) {
      clearTimeout(this.auditDebounceTimer)
    }
  },

  methods: {
    getPersistedInviteAuthorization() {
      const raw = window.localStorage.getItem('wspinvite-auth') || window.sessionStorage.getItem('wspinvite-auth')
      if (!raw) return null

      try {
        const payload = JSON.parse(raw)
        if (!payload || typeof payload.inviteCode !== 'string' || typeof payload.expiresAt !== 'number') {
          window.localStorage.removeItem('wspinvite-auth')
          window.sessionStorage.removeItem('wspinvite-auth')
          return null
        }

        if (payload.expiresAt <= Date.now()) {
          window.localStorage.removeItem('wspinvite-auth')
          window.sessionStorage.removeItem('wspinvite-auth')
          return null
        }

        return payload
      } catch (e) {
        window.localStorage.removeItem('wspinvite-auth')
        window.sessionStorage.removeItem('wspinvite-auth')
        return null
      }
    },

    handleFrameLoad() {
      this.loading = false
      // 认证通过后将邀请码传给 iframe
      if (this.currentInviteCode) {
        this.postInviteCodeToFrame(this.currentInviteCode, this.inviteExpiresAt)
      }
    },

    persistInviteAuthorization(inviteCode, expiresAt) {
      if (typeof expiresAt !== 'number') {
        return
      }
      const payload = {
        inviteCode,
        authorizedAt: Date.now(),
        expiresAt
      }
      window.localStorage.setItem('wspinvite-auth', JSON.stringify(payload))
      window.sessionStorage.setItem('wspinvite-auth', JSON.stringify(payload))
    },

    /**
     * 处理来自 iframe 的消息
     */
    handleMessage(event) {
      if (event.origin !== window.location.origin) return
      const data = event.data
      if (!data || !data.type) return

      if (data.type === 'WSP_AUTH_EXPIRED') {
        // iframe 通知认证过期，跳回登录页
        this.$router.push({ name: 'login' })
        return
      }

      if (data.type === 'WSP_PRICE_DATA') {
        // 接收明细数据，防抖500ms后保存审计记录
        this.onPriceDataReceived(data)
      }
    },

    /**
     * 将邀请码发送给 iframe
     */
    postInviteCodeToFrame(inviteCode, expiresAt) {
      if (typeof expiresAt !== 'number') {
        return
      }
      const frame = this.$refs.selectorFrame
      if (frame && frame.contentWindow) {
        frame.contentWindow.postMessage({ type: 'WSP_INVITE_CODE', inviteCode, expiresAt }, window.location.origin)
      }
    },

    /**
     * 明细数据变化时，程互需收需保存审计
     * 优先使用 auditSnapshot（完整種帡）而不是仰赖 price data
     */
    onPriceDataReceived(data) {
      // 不需要防抖，直接保存
      this.saveAuditRecord(data)
    },

    saveAuditRecord(data) {
      const inviteCode = data.inviteCode || this.currentInviteCode
      if (!inviteCode) return

      // 优先使用 auditSnapshot（完整快照）不依赖 cart 中间结构
      // 黝死不会因为 selector 代码遗漏部分维保项目而导致审计不完整
      const auditSnapshot = data.auditSnapshot || {}
      const priceData = data.data || {}

      const content = JSON.stringify({
        snapshot: auditSnapshot,
        priceData: priceData,
        recordedAt: new Date().toISOString(),
      })

      createOpAudit({ inviteCode, content })
        .then(res => {
          if (res.data && res.data.success === false) {
            console.warn('[WSP] 审计记录保存失败：', res.data.msg)
          }
        })
        .catch(err => {
          console.warn('[WSP] 审计记录保存异常：', err)
        })
    },

    // ========== 邀请码认证 ==========
    handleValidate() {
      this.$refs.authForm.validate(valid => {
        if (!valid) return
        this.authError = ''
        this.authLoading = true
        validateInviteCode({ inviteCode: this.authForm.inviteCode.trim() })
          .then(res => {
            if (res.data && res.data.success !== false && res.data.content) {
              // 认证成功
              const inviteCode = this.authForm.inviteCode.trim()
              const info = res.data.content
              const expiresAt = this.calcInviteExpiresAt(info)
              this.currentInviteCode = inviteCode
              this.inviteExpiresAt = expiresAt
              this.persistInviteAuthorization(inviteCode, expiresAt)
              this.authPassed = true
              this.frameSrc = '/selector/'
              this.showAuthDialog = false
              this.$message.success(`认证成功，邀请码剩余有效期约 ${this.calcRemainHours(info)} 小时`)
            } else {
              this.authError = res.data?.msg || '认证失败，请检查邀请码是否正确'
            }
          })
          .catch(err => {
            console.error(err)
            const msg = err?.response?.data?.msg || err?.message || '认证失败，请检查邀请码'
            this.authError = msg
          })
          .finally(() => {
            this.authLoading = false
          })
      })
    },

    handleAuthCancel() {
      this.showAuthDialog = false
      this.authForm.inviteCode = ''
      this.authError = ''
      // 点击取消时返回首页
      this.$router.push('/')
    },

    /**
     * 根据 createTime 和 expireHours 计算剩余小时数
     */
    calcRemainHours(info) {
      if (!info || !info.expireHours) return info?.expireHours || '-'
      if (!info.createTime) return info.expireHours
      const expireMs = new Date(info.createTime).getTime() + info.expireHours * 3600000
      const remainMs = expireMs - Date.now()
      if (remainMs <= 0) return 0
      return Math.max(1, Math.floor(remainMs / 3600000))
    },

    calcInviteExpiresAt(info) {
      const expireHours = Number(info && info.expireHours)
      const fallbackExpiresAt = Date.now() + 24 * 60 * 60 * 1000
      if (!Number.isFinite(expireHours) || expireHours <= 0) {
        return fallbackExpiresAt
      }

      if (!info || !info.createTime) {
        return Date.now() + expireHours * 3600000
      }

      const createTimeMs = new Date(info.createTime).getTime()
      if (!Number.isFinite(createTimeMs)) {
        return Date.now() + expireHours * 3600000
      }

      return createTimeMs + expireHours * 3600000
    }
  }
}
</script>

<style scoped>
.vdi-product-selector-page {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.selector-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 100%;
  color: #909399;
  font-size: 14px;
}

.selector-loading .el-icon-loading {
  font-size: 32px;
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.selector-frame {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

/* 认证弹窗 - 自定义样式 */
.auth-dialog >>> .el-dialog {
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.auth-dialog >>> .el-dialog__body {
  padding: 0;
}

.auth-dialog >>> .el-dialog__header {
  display: none;
}

.auth-dialog >>> .el-dialog__footer {
  border-top: 1px solid #ebeef5;
  padding: 12px 24px;
  text-align: center;
}

/* 认证弹窗内容 */
.auth-dialog-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px 24px;
  position: relative;
}

.auth-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}

.auth-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #fff;
}

.auth-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #303133;
}

.auth-description {
  margin: 0;
  font-size: 13px;
  color: #909399;
  line-height: 1.6;
}

/* 表单 */
.auth-form {
  width: 100%;
  margin: 8px 0;
}

.auth-form >>> .el-form-item {
  margin-bottom: 0;
}

.auth-form >>> .el-input__inner {
  border-radius: 6px;
  border: 1px solid #dcdfe6;
  font-size: 14px;
  padding: 10px 15px;
}

.auth-form >>> .el-input__inner:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
}

/* 错误提示 */
.auth-error {
  background: #fef0f0;
  border: 1px solid #fde2e2;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 13px;
  color: #f56c6c;
  display: flex;
  align-items: center;
  gap: 6px;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-8px);
  opacity: 0;
}

.auth-footer {
  display: flex;
  justify-content: center;
  margin-top: 4px;
}

.auth-submit {
  width: 140px;
  height: 42px;
  font-size: 14px;
  border-radius: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 6px;
}

.auth-submit:hover {
  opacity: 0.9;
}

.auth-cancel {
  width: 140px;
  height: 42px;
  font-size: 14px;
  border-radius: 6px;
  background: #f0f2f5;
  border: 1px solid #dcdfe6;
  color: #606266;
  transition: all 0.3s;
}

.auth-cancel:hover {
  background: #e4e7ed;
  color: #303133;
}
</style>
